"use client";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Button } from "../ui/button";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../ui/dropdown-menu";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { 
  Plus, 
  FileText, 
  Link, 
  Upload, 
  ExternalLink, 
  X,
  Loader2,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered
} from "lucide-react";
import { LinkDocumentDialog } from "../planning/LinkDocumentDialog";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface DocumentItem {
  id: string;
  type: 'textbox' | 'linked' | 'uploaded';
  name: string;
  reference?: string;
  url?: string;
  content?: string; // For textbox
  textResponse?: { html: string; plainText: string }; // For textbox with rich text
}

interface ControlDocumentManagerProps {
  controlId: string;
  workspaceLinked: string[]; // Array of workspace names
  documents: DocumentItem[];
  onDocumentsChange: (documents: DocumentItem[]) => void;
  textResponse?: { html: string; plainText: string };
  onTextResponseChange?: (textResponse: { html: string; plainText: string }) => void;
  disabled?: boolean;
  label?: string;
}

const ControlDocumentManagerComponent: React.FC<ControlDocumentManagerProps> = ({ 
  controlId,
  workspaceLinked,
  documents,
  onDocumentsChange,
  textResponse,
  onTextResponseChange,
  disabled = false,
  label
}) => {
  const { isArchivalMode } = useArchivalMode();
  const effectiveDisabled = disabled || isArchivalMode;
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showTextbox, setShowTextbox] = useState(false);
  const [textboxContent, setTextboxContent] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasTextbox, setHasTextbox] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const textboxEditorRef = useRef<HTMLDivElement>(null);
  
  // Stabilize callbacks with refs to prevent infinite loops
  const onDocumentsChangeRef = useRef(onDocumentsChange);
  const onTextResponseChangeRef = useRef(onTextResponseChange);
  
  // Update refs when callbacks change
  useEffect(() => {
    onDocumentsChangeRef.current = onDocumentsChange;
    onTextResponseChangeRef.current = onTextResponseChange;
  }, [onDocumentsChange, onTextResponseChange]);

  // Initialize textbox content from textResponse
  useEffect(() => {
    if (textResponse?.html) {
      setTextboxContent(textResponse.html);
      setHasTextbox(true);
      setShowTextbox(true); // Always show editor when there's content
    } else if (textResponse?.plainText) {
      setTextboxContent(textResponse.plainText);
      setHasTextbox(true);
      setShowTextbox(true);
    } else {
      setHasTextbox(false);
      // Only show editor if user explicitly clicked "Add" -> "Textbox"
    }
  }, [textResponse]);

  // Update contentEditable div when textboxContent changes (but not during editing)
  useEffect(() => {
    if (textboxEditorRef.current && (showTextbox || hasTextbox)) {
      // Only update if the content is different to avoid cursor jumping
      // Check if user is currently typing (has focus)
      const isFocused = document.activeElement === textboxEditorRef.current;
      if (!isFocused && textboxEditorRef.current.innerHTML !== textboxContent) {
        textboxEditorRef.current.innerHTML = textboxContent || '';
      }
    }
  }, [textboxContent, showTextbox, hasTextbox]);

  // Helper function to get phase name and workspace prefix from Sections.json
  const getWorkspaceInfo = async (workspaceName: string): Promise<{ phase: string; prefix: string } | null> => {
    try {
      if (window.sharePointAPI?.downloadSectionsJson) {
        const result = await window.sharePointAPI.downloadSectionsJson();
        if (result?.success && result?.data) {
          const sectionsData = result.data;
          
          // Check all phases: Planning, Execution, ConcludingProcedures
          const phases = ['Planning', 'Execution', 'ConcludingProcedures'] as const;
          
          for (const phase of phases) {
            if (sectionsData[phase] && sectionsData[phase][workspaceName]) {
              const prefix = sectionsData[phase][workspaceName];
              return { phase, prefix };
            }
          }
        }
      }
    } catch (error) {
      console.error(`Error getting workspace info for ${workspaceName}:`, error);
    }
    
    return null;
  };

  // Get section names for all linked workspaces
  const getSectionNames = async (): Promise<string[]> => {
    if (!workspaceLinked || workspaceLinked.length === 0) {
      return [];
    }

    const sectionNames: string[] = [];
    
    // Get info for each workspace
    for (const workspaceName of workspaceLinked) {
      const workspaceInfo = await getWorkspaceInfo(workspaceName);
      if (workspaceInfo) {
        const sectionName = `${workspaceInfo.phase}_${workspaceInfo.prefix}_${controlId}`;
        sectionNames.push(sectionName);
      }
    }
    
    return sectionNames;
  };

  const handleAddTextbox = () => {
    setShowTextbox(true);
    setHasTextbox(true);
  };

  const handleShowLinkDialog = () => {
    setShowLinkDialog(true);
  };

  const handleClearTextbox = () => {
    setTextboxContent('');
    if (onTextResponseChangeRef.current) {
      onTextResponseChangeRef.current({
        html: '',
        plainText: ''
      });
    }
    setHasTextbox(false);
    setShowTextbox(false);
  };

  const handleLinkDocument = useCallback(async (selectedDocuments: any[]) => {
    if (selectedDocuments.length === 0) return;
    
    const newDocuments = selectedDocuments.map(doc => ({
      id: doc.id || `doc-${Date.now()}-${Math.random()}`,
      type: 'linked' as const,
      name: doc.name,
      url: doc.url,
      reference: doc.reference
    }));
    
    // Update local state
    const updatedDocuments = [...documents, ...newDocuments];
    onDocumentsChangeRef.current(updatedDocuments);
    setShowLinkDialog(false);
    
    // Update db.json with section names for all workspaces
    setTimeout(async () => {
      setIsLinking(true);
      try {
        const sectionNames = await getSectionNames();
        const fileNames = selectedDocuments.map(doc => doc.name);
        
        // Add sections sequentially for each workspace
        const updatePromises = sectionNames.map(sectionName => {
          return window.sharePointAPI?.updateFileSections({
            fileNames,
            sectionToAdd: sectionName
          });
        });
        
        await Promise.all(updatePromises);
        console.log('Updated sections for all workspaces:', sectionNames);
      } catch (error) {
        console.error('Error updating db.json:', error);
      } finally {
        setIsLinking(false);
      }
    }, 500);
  }, [documents]);

  const handleUploadFile = useCallback(async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "*/*";
    
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return;
      
      setIsUploading(true);
      
      try {
        const sectionNames = await getSectionNames();
        const fyYear = getFY_YEAR();
        
        // Process all files and collect new documents
        const uploadPromises = files.map((file) => {
          return new Promise<DocumentItem | null>((resolve) => {
            try {
              const reader = new FileReader();
              reader.onload = async () => {
                try {
                  const base64Content = reader.result as string;
                  const base64Data = base64Content.split(',')[1];
                  
                  // Upload to SharePoint with all section names
                  if (window.sharePointAPI?.uploadFile) {
                    const uploadResult = await window.sharePointAPI.uploadFile({
                      fileName: file.name,
                      fileContent: base64Data,
                      folderName: 'juggernaut',
                      referenceValue: `${controlId} - ${label || 'Document'}`,
                      sectionList: sectionNames,
                      fyYear: fyYear
                    });
                    
                    if (uploadResult?.success) {
                      const newDocument: DocumentItem = {
                        id: `doc-${Date.now()}-${Math.random()}`,
                        type: 'uploaded' as const,
                        name: file.name,
                        url: uploadResult.data?.webUrl || uploadResult.webUrl || uploadResult.url,
                        reference: uploadResult.data?.reference || uploadResult.reference
                      };
                      
                      resolve(newDocument);
                    } else {
                      console.error('Upload failed for file:', file.name, uploadResult.error);
                      resolve(null);
                    }
                  } else {
                    resolve(null);
                  }
                } catch (error) {
                  console.error('Error uploading file:', file.name, error);
                  resolve(null);
                }
              };
              reader.onerror = () => {
                console.error('Failed to read file:', file.name);
                resolve(null);
              };
              reader.readAsDataURL(file);
            } catch (error) {
              console.error('Error processing file:', file.name, error);
              resolve(null);
            }
          });
        });
        
        // Wait for all uploads to complete
        const uploadResults = await Promise.all(uploadPromises);
        const newDocuments = uploadResults.filter((doc): doc is DocumentItem => doc !== null);
        
        // Update state with all new documents at once
        if (newDocuments.length > 0) {
          const updatedDocuments = [...documents, ...newDocuments];
          onDocumentsChangeRef.current(updatedDocuments);
        }
      } catch (error) {
        console.error('Error getting section names:', error);
      } finally {
        setIsUploading(false);
      }
    };
    
    input.click();
  }, [controlId, documents, label, workspaceLinked]);

  const handleRemoveDocument = useCallback(async (docId: string) => {
    // Find the document being removed
    const documentToRemove = documents.find(doc => doc.id === docId);
    if (!documentToRemove) {
      console.warn('Document not found for removal:', docId);
      return;
    }
    
    const fileName = documentToRemove.name;
    
    if (!fileName) {
      console.error('Cannot remove document: fileName is missing');
      return;
    }
    
    // Remove from local state immediately
    const updatedDocuments = documents.filter(doc => doc.id !== docId);
    onDocumentsChangeRef.current(updatedDocuments);
    
    // Remove sections from db.json for all workspaces
    if (window.sharePointAPI?.removeFileSection) {
      setIsLinking(true);
      try {
        const sectionNames = await getSectionNames();
        
        if (sectionNames.length === 0) {
          console.warn('No section names found for workspaces, cannot remove sections from db.json');
          console.log('WorkspaceLinked:', workspaceLinked);
          console.log('ControlId:', controlId);
          setIsLinking(false);
          return;
        }
        
        console.log(`Removing sections from db.json for file "${fileName}":`, sectionNames);
        
        // Remove sections sequentially for each workspace to avoid race conditions with db.json
        for (const sectionName of sectionNames) {
          try {
            const result = await window.sharePointAPI.removeFileSection({
              fileName,
              sectionToRemove: sectionName
            });
            if (result?.success) {
              console.log(` Successfully removed section "${sectionName}" from file "${fileName}" in db.json`);
            } else {
              console.error(` Failed to remove section "${sectionName}" from file "${fileName}":`, result?.error);
            }
          } catch (error) {
            console.error(` Error removing section "${sectionName}" from file "${fileName}":`, error);
          }
        }
        
        console.log(` Completed removal of sections from db.json for file "${fileName}":`, sectionNames);
      } catch (error) {
        console.error(' Error removing sections from db.json:', error);
      } finally {
        setIsLinking(false);
      }
    } else {
      console.error(' Cannot remove sections: removeFileSection API not available');
    }
  }, [controlId, documents, workspaceLinked]);

  const handleRemoveTextbox = useCallback(() => {
    if (onTextResponseChangeRef.current) {
      onTextResponseChangeRef.current({
        html: '',
        plainText: ''
      });
    }
    setTextboxContent('');
    setHasTextbox(false);
    setShowTextbox(false);
  }, []);

  return (
    <div className="space-y-3">
      {/* Display linked/uploaded documents */}
      {documents.length > 0 && (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between p-3 border border-white/10 rounded-lg bg-black/20">
              <div className="flex items-center gap-2 flex-1">
                {doc.type === 'linked' ? (
                  <Link className="h-4 w-4 text-blue-400" />
                ) : (
                  <Upload className="h-4 w-4 text-green-400" />
                )}
                {doc.url ? (
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-400 hover:text-blue-300 hover:underline flex-1"
                  >
                    {doc.name}
                  </a>
                ) : (
                  <span className="text-sm text-white flex-1">{doc.name}</span>
                )}
              </div>
              {!effectiveDisabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveDocument(doc.id)}
                  className="text-red-400 hover:text-red-300 h-6 px-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Document Dropdown */}
      <div className="flex items-center gap-2">
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-white/20 bg-black/40 text-white hover:bg-white/10"
              disabled={effectiveDisabled}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="border-white/20 bg-black/90 text-white z-50" align="start">
            <DropdownMenuItem onClick={() => { setDropdownOpen(false); setTimeout(() => handleAddTextbox(), 100); }} className="hover:bg-white/10 cursor-pointer" disabled={effectiveDisabled}>
              <FileText className="h-4 w-4 mr-2" />
              {hasTextbox ? 'Edit Textbox' : 'Textbox'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setDropdownOpen(false); setTimeout(() => handleShowLinkDialog(), 100); }} className="hover:bg-white/10 cursor-pointer" disabled={effectiveDisabled}>
              <Link className="h-4 w-4 mr-2" />
              Link existing workpaper
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setDropdownOpen(false); setTimeout(() => handleUploadFile(), 100); }} className="hover:bg-white/10 cursor-pointer" disabled={effectiveDisabled || isUploading}>
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? 'Uploading...' : 'Upload file'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Always show inline textbox editor if there's content or if showTextbox is true */}
      {(hasTextbox || showTextbox) && (
        <div className="mt-3 p-4 border border-white/10 rounded bg-black/20">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-white">
                Text Document
              </h4>
              <div className="flex items-center gap-2">
                {hasTextbox && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearTextbox}
                    className="border-red-500/20 bg-transparent text-red-400 hover:bg-red-500/10"
                    disabled={effectiveDisabled}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            
            {/* Inline Rich Text Editor */}
            <div className="space-y-2">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 p-2 border border-white/10 rounded bg-black/40">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('bold')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <Bold className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('italic')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <Italic className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('underline')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <Underline className="h-4 w-4" />
                </Button>
                
                <div className="w-px h-6 bg-white/20 mx-1" />
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('insertUnorderedList')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => document.execCommand('insertOrderedList')}
                  className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                  disabled={effectiveDisabled}
                >
                  <ListOrdered className="h-4 w-4" />
                </Button>
              </div>

              {/* Editor */}
              <div
                ref={textboxEditorRef}
                contentEditable={!effectiveDisabled}
                onInput={(e) => {
                  const html = e.currentTarget.innerHTML;
                  setTextboxContent(html);
                  // Immediately call onTextResponseChange to update parent state
                  if (onTextResponseChangeRef.current) {
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = html;
                    const plainText = tempDiv.textContent || tempDiv.innerText || '';
                    onTextResponseChangeRef.current({
                      html: html || '',
                      plainText: plainText
                    });
                  }
                  // Always keep editor visible and editable when user types
                  setHasTextbox(true);
                  setShowTextbox(true);
                }}
                className="min-h-[200px] p-3 border border-white/10 rounded bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 prose prose-invert max-w-none"
                style={{ 
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}
                suppressContentEditableWarning
              />
            </div>
          </div>
        </div>
      )}

      {/* Link Document Dialog */}
      <LinkDocumentDialog
        isOpen={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        onLink={handleLinkDocument}
      />

      {/* Loading Indicator */}
      {(isLinking || isUploading) && (
        <div className="fixed bottom-4 right-4 bg-black/80 text-white px-4 py-2 rounded-md shadow-lg flex items-center gap-2 z-50 border border-white/20">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">
            {isLinking ? 'Updating SharePoint...' : 'Uploading file...'}
          </span>
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const ControlDocumentManager = React.memo(ControlDocumentManagerComponent);

